import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { RouteFocusManager } from "./components/RouteFocusManager";
import { LoginPage } from "./pages/LoginPage";
import { SignupPage } from "./pages/SignupPage";
import { VerifyEmailPage } from "./pages/VerifyEmailPage";
import { LeadsPage } from "./pages/LeadsPage";
import { LeadDetailPage } from "./pages/LeadDetailPage";
import { NewLeadPage } from "./pages/NewLeadPage";
import { SettingsPage } from "./pages/SettingsPage";
import { EmbedPage } from "./pages/EmbedPage";
import { TemplatesPage } from "./pages/TemplatesPage";
import { TemplateEditorPage } from "./pages/TemplateEditorPage";
import { BillingPage } from "./pages/BillingPage";
// round7/master-admin-panel-ui (P4.6-B) — the platform-admin panel, D-7's
// "route tree inside DockPro-Portal, code-split". AdminPanel is a tiny eager
// wrapper around a lazy() boundary (see its own header): every tenant-facing
// page stays eagerly bundled exactly as before, and a builder never
// downloads the admin chunk at all.
import { AdminPanel } from "./pages/admin/AdminPanel";
// round7/org-admin-panel-ui (P4.6-C) — the org-admin section, code-split the
// same way. Unlike /admin this is the SAME identity plane as the tenant pages
// (an `orgAdmin` is a custom:role on the same Cognito app client), so it is
// gated by the existing ProtectedRoute with scope="org" rather than by a
// second auth context.
import { OrganizationPanel } from "./pages/organization/OrganizationPanel";
import { WelcomePage } from "./pages/WelcomePage";
import "./styles/global.css";

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <AuthProvider>
      <BrowserRouter>
        <RouteFocusManager />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          {/* round7/signup-ui (P4.5-B) — public, pre-auth routes, same
              "not wrapped in ProtectedRoute" pattern as /login above. */}
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          {/* Stripe's success_url for the card-gated trial lands here —
              still pre-auth (the visitor has a card on file but has not
              verified their email yet, so they cannot sign in). */}
          <Route path="/welcome" element={<WelcomePage />} />
          <Route path="/leads" element={
            <ProtectedRoute><LeadsPage /></ProtectedRoute>
          } />
          {/* round11/portal-create-lead — literal segment, declared before
              the :idempotencyKey parameter route below. */}
          <Route path="/leads/new" element={
            <ProtectedRoute><NewLeadPage /></ProtectedRoute>
          } />
          <Route path="/leads/:idempotencyKey" element={
            <ProtectedRoute><LeadDetailPage /></ProtectedRoute>
          } />
          {/* round9/design-pass (Direction A) — Settings is ONE page with a
              left rail; the trailing splat lets the rail's section live in
              the URL (/settings/rate-card, /settings/quoting,
              /settings/engineering) while SettingsPage stays mounted across
              section switches, so one form's drafts survive navigation and
              one Save still covers every section. See
              components/shell/settingsSections.ts. */}
          <Route path="/settings/*" element={
            <ProtectedRoute><SettingsPage /></ProtectedRoute>
          } />
          {/* The pre-design-pass route. Bookmarks and the backend's own
              docs still say /rate-card; keep it working forever, cheaply. */}
          <Route path="/rate-card" element={<Navigate to="/settings/rate-card" replace />} />
          {/* round9/embed-config — everything the embedded designer OFFERS a
              homeowner (designer preset, tools, options, materials, custom
              slip sizes), on its own page with its own Save. Sits on the
              Settings rail (design-pass) but keeps its own route because it
              is its own form with its own PUT keys and validation. Owner-
              only in practice (server-enforced; a rep sees read-only
              summaries). */}
          <Route path="/embed" element={
            <ProtectedRoute><EmbedPage /></ProtectedRoute>
          } />
          {/* round9/design-pass — Templates promoted from a card at the foot
              of Rate Card to a top-level destination (Direction A). */}
          <Route path="/templates" element={
            <ProtectedRoute><TemplatesPage /></ProtectedRoute>
          } />
          {/* round8/template-authoring — template authoring/editing is its
              own destination rather than a card inside a lead, and needs the
              viewport for a real dock designer. Entry points live on
              /templates; both are owner-only (enforced server-side, and the
              page itself shows a rep a read-only explanation instead of a
              form that would 403). */}
          <Route path="/templates/new" element={
            <ProtectedRoute><TemplateEditorPage /></ProtectedRoute>
          } />
          <Route path="/templates/:templateId/edit" element={
            <ProtectedRoute><TemplateEditorPage /></ProtectedRoute>
          } />
          <Route path="/billing" element={
            <ProtectedRoute><BillingPage /></ProtectedRoute>
          } />
          {/* P4.6-C — org admins only (ProtectedRoute scope="org" sends an
              owner/rep back to /leads). Trailing splat so the organization
              tree owns the shape of its own subtree, same as /admin below. */}
          <Route path="/organization/*" element={
            <ProtectedRoute scope="org"><OrganizationPanel /></ProtectedRoute>
          } />
          {/* P4.6-B — must precede the catch-all below, and matches with a
              trailing splat so the admin tree owns the shape of its own
              subtree (main.tsx stays ignorant of it). */}
          <Route path="/admin/*" element={<AdminPanel />} />
          {/* Unchanged by P4.6-C, deliberately. An org admin landing here
              goes to /leads, whose ProtectedRoute immediately redirects them
              to /organization — two hops, no broken page, and no role logic
              duplicated into main.tsx. LoginPage sends them straight to
              /organization on sign-in, so this only ever fires for a stray
              URL. */}
          <Route path="*" element={<Navigate to="/leads" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  </StrictMode>,
);
